// Staff Performance service — API_INTEGRATION.md §3.37
// Staff activity logs and sales performance.
// Vertical: SHARED. No feature flag.

import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface StaffPerformance {
  user_id: string;
  user_name: string;
  role: string;
  total_sales: number;     // paise
  bill_count: number;
  avg_bill_value: number;  // paise
  returns_count: number;
  returns_value: number;   // paise
  discounts_given: number; // paise
  commission_earned: number; // paise
  top_products_sold: Array<{ product_name: string; quantity_sold: number; revenue: number }>;
  hourly_performance: Array<{ hour: number; bill_count: number; revenue: number }>;
}

export interface StaffActivityEntry {
  id: string;
  action: string;
  path: string;
  resource_id: string | null;
  changes: Record<string, unknown> | null;
  ip: string | null;
  duration_ms: number | null;
  created_at: string;
}

export interface StaffActivityData {
  user_id: string;
  user_name: string;
  role: string;
  entries: StaffActivityEntry[];
}

export interface StaffPerformanceParams {
  start_date?: string;
  end_date?: string;
}

export interface StaffListParams {
  page?: number;
  limit?: number;
  role?: string;
  location_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface StaffPerformanceRow {
  user_id: string;
  user_name: string;
  role: string;
  total_sales: number;     // paise
  bill_count: number;
  avg_bill_value: number;  // paise
  returns_count: number;
  returns_value: number;   // paise
  discounts_given: number; // paise
  commission_earned: number; // paise
}

export interface StaffLeaderboardEntry {
  user_id: string;
  user_name: string;
  role: string;
  total_sales: number;     // paise
  bill_count: number;
  avg_bill_value: number;  // paise
}

export interface StaffPerformanceSummary {
  total_sales: number;     // paise (tenant-wide, filtered window)
  bill_count: number;
  avg_bill_value: number;  // paise
  active_staff: number;    // count of staff with at least one bill in window
  top_performer: { user_id: string; user_name: string; total_sales: number } | null;
  // Tenant-wide top 10 (filtered window) — drives the leaderboard / podium /
  // distribution so they don't rescale to the current page.
  top_performers: StaffLeaderboardEntry[];
}

export interface StaffPerformanceListResponse {
  rows: StaffPerformanceRow[];
  summary: StaffPerformanceSummary;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const staffService = {
  async getPerformance(
    userId: string,
    params?: StaffPerformanceParams,
  ): Promise<StaffPerformance> {
    const q = buildQuery({
      start_date: params?.start_date,
      end_date: params?.end_date,
    });
    return apiClient<StaffPerformance>(`/staff/${userId}/sales-performance${q}`);
  },

  async getActivity(
    userId: string,
    params?: StaffPerformanceParams & { page?: number; limit?: number },
  ): Promise<{
    data: StaffActivityData;
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      start_date: params?.start_date,
      end_date: params?.end_date,
      page: params?.page,
      limit: params?.limit ?? 20,
    });
    return apiClientPaginated<StaffActivityData>(`/staff/${userId}/activity${q}`);
  },

  async listPerformance(
    params?: StaffListParams,
  ): Promise<{
    data: StaffPerformanceListResponse;
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      start_date: params?.start_date,
      end_date: params?.end_date,
      role: params?.role,
      location_id: params?.location_id,
      page: params?.page,
      limit: params?.limit ?? 20,
    });
    return apiClientPaginated<StaffPerformanceListResponse>(`/staff/performance${q}`);
  },
};

export { ApiError };
